import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDistrictUsers, addTeamMember, removeTeamMember, formatApiError } from '../services/api';
import { 
  ArrowLeft, Users, UserPlus, Clock,
  Crown, User, CheckCircle, AlertCircle, Loader2, Trash2
} from 'lucide-react';

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: 'Welcome2026!', role: 'user' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchMembers = async () => {
    try {
      const response = await getDistrictUsers();
      setMembers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await addTeamMember(form);
      setSuccess(`Account created for ${form.email}. Share the password with them: ${form.password}`);
      setForm({ name: '', email: '', password: 'Welcome2026!', role: 'user' });
      fetchMembers();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Remove ${member.name} from your district?`)) return;
    try {
      await removeTeamMember(member._id);
      setMembers((prev) => prev.filter((m) => m._id !== member._id));
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const canManage = user?.role === 'admin' || user?.role === 'district_admin';

  const getRoleBadge = (role) => {
    const styles = {
      admin: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Admin' },
      district_admin: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'District Admin' },
      user: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Member' },
      staff: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Staff' },
    };
    const style = styles[role] || styles.user;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {role === 'admin' || role === 'district_admin' ? <Crown className="w-3 h-3" /> : <User className="w-3 h-3" />}
        {style.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div>
              <h1 className="font-outfit text-xl font-bold text-slate-900">Team</h1>
              <p className="text-sm text-slate-500">{user?.district_name || 'Your District'}</p>
            </div>
          </div>
          {canManage && (
            <button
              onClick={() => setShowInvite(!showInvite)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-all text-sm"
              data-testid="invite-member-btn"
            >
              <UserPlus className="w-4 h-4" />
              Add Member
            </button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Add Member Panel */}
        {showInvite && (
          <form onSubmit={handleAddMember} className="bg-white border border-blue-200 rounded-xl p-6 mb-6 animate-in" data-testid="invite-panel">
            <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-1">Add a Team Member</h3>
            <p className="text-sm text-slate-500 mb-4">
              Create an account under your district. <b>Members</b> get full access to assessments, scorecards, and roadmaps.
              <b> Staff</b> can log incidents and take training only — ideal for principals, nurses, and facilities leads.
            </p>
            {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="add-member-error"><AlertCircle className="w-4 h-4" />{error}</div>}
            {success && <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="add-member-success"><CheckCircle className="w-4 h-4" />{success}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Full name"
                className="px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="member-name-input" />
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="email@district.edu"
                className="px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="member-email-input" />
              <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} placeholder="Initial password"
                className="px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="member-password-input" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="member-role-select">
                <option value="user">Member — full access</option>
                <option value="staff">Staff — incidents & training only</option>
              </select>
            </div>
            <button type="submit" disabled={saving} className="mt-4 px-5 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2" data-testid="add-member-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Create Account
            </button>
          </form>
        )}

        {/* Seat Usage */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="seat-usage">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-outfit text-lg font-semibold text-slate-900">Team Members</h3>
            <span className="text-sm text-slate-500">{members.length} active</span>
          </div>
        </div>

        {/* Members List */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="members-list">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="font-outfit text-lg font-semibold text-slate-900">Members ({members.length})</h3>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500 animate-pulse">Loading team members...</div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>No team members yet. Invite someone to get started.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {members.map((member) => (
                <div 
                  key={member._id} 
                  className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-all"
                  data-testid={`member-${member.email}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-white font-medium text-sm">
                      {member.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{member.name}</p>
                      <p className="text-sm text-slate-500">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getRoleBadge(member.role)}
                    <div className="text-right">
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {member.created_at ? new Date(member.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    {canManage && !['admin', 'district_admin'].includes(member.role) && (
                      <button onClick={() => handleRemove(member)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`remove-member-${member.email}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
